'use client'

import { useEffect, useRef, useState } from 'react'

import Button from '@/components/Button/Button'

import { useAuth } from './AuthContext'

import styles from './VerifyForm.module.scss'
import classNames from 'classnames/bind'

const classes = classNames.bind(styles)

export default function MFAForm() {
    const { submitMfaCode, setError, mfaSession, resendMfaCode } = useAuth()
    const boxes = useRef([])

    const [code, setCode] = useState('')
    const [loading, setLoading] = useState(false)
    const [resending, setResending] = useState(false)
    const [codeTimer, setCodeTimer] = useState(0)

    useEffect(() => {
        if (codeTimer > 0) {
            setTimeout(() => {
                setCodeTimer(codeTimer - 1)
            }, 1000)
        }
    }, [codeTimer])

    useEffect(() => {
        if (code.length === 6) {
            submit()
        }
    }, [code])

    const isSMS = mfaSession?.challengeName === 'SMS_MFA'

    const submit = async () => {
        if (loading) return
        setLoading(true)
        setError(null)
        try {
            await submitMfaCode(code)
        } finally {
            setLoading(false)
        }
    }

    const handleResendCode = async () => {
        if (resending || loading) return
        setResending(true)
        setCodeTimer(30)
        setError(null)
        // Clear the code input
        boxes.current.forEach((box) => {
            box.value = ''
        })
        setCode('')
        try {
            await resendMfaCode()
        } finally {
            setResending(false)
        }
    }

    return (
        <form
            className={classes('verify-form')}
            onSubmit={(e) => {
                e.preventDefault()
                if (code.length === 6) {
                    submit()
                }
            }}
            autoComplete="off"
        >
            <h1 className={classes('title')}>Two-Factor Authentication</h1>
            <p className={classes('subtext')}>
                {isSMS
                    ? 'Please enter the 6-digit verification code sent to your phone via SMS.'
                    : 'Please enter the 6-digit verification code from your authenticator app.'}
            </p>
            <div
                className={classes('verification-input-container')}
                onKeyDown={(e) => {
                    processBoxMovement(e)
                    setTimeout(() => {
                        setCode(boxes.current.map((box) => box.value).join(''))
                    }, 50)
                }}
            >
                {new Array(6).fill(0).map((_, i) => {
                    return (
                        <input
                            key={`code-box-${i}`}
                            className={classes('verify-input')}
                            type="number"
                            maxLength={1}
                            ref={(el) => (boxes.current[i] = el)}
                            autoComplete="off"
                            min="0"
                            max="9"
                            onChange={({ target }) => {
                                if (target.value.length > 1) {
                                    const characterArray = target.value.split('')
                                    let element = target
                                    let count = 0
                                    while (element) {
                                        element.value = characterArray[count]
                                        element = element.nextElementSibling
                                        count++
                                    }
                                }
                                setTimeout(() => {
                                    setCode(boxes.current.map((box) => box.value).join(''))
                                }, 50)
                            }}
                        />
                    )
                })}
            </div>
            <div className={classes('clear-button')}>
                <span
                    onClick={(e) => {
                        boxes.current.forEach((box) => {
                            box.value = ''
                        })
                        boxes.current[0].focus()
                        setCode('')
                    }}
                >
                    Clear
                </span>
            </div>

            <Button
                className={classes('verify-button')}
                disabled={code.length !== 6 || loading}
                text={loading ? 'Verifying...' : 'Verify'}
            />
            {isSMS && (
                <div className={classes('resend-code-wrapper')}>
                    {codeTimer === 0 ? (
                        <span>
                            Not received a code?{' '}
                            <span
                                className={classes('resend-code', { disabled: resending || loading })}
                                onClick={handleResendCode}
                            >
                                {resending ? 'Sending...' : 'Click here to resend'}
                            </span>
                        </span>
                    ) : (
                        <span className={classes('timer')}>
                            Sent! Please wait {codeTimer} seconds before trying again.
                        </span>
                    )}
                </div>
            )}
        </form>
    )
}

const processBoxMovement = async (e) => {
    if (!e.ctrlKey && e.key.length === 1) {
        e.preventDefault()
    }
    if (e.target.value.length === 0 && /^[0-9]{1}/.test(e.key)) {
        e.target.value = e.key

        try {
            e.target.nextElementSibling.focus()
        } catch (e) {}
    }
    if (e.key === 'Backspace') {
        setTimeout(() => {
            try {
                e.target.previousElementSibling.focus()
            } catch (e) {}
        }, 10)
    }

    if ((e.key === 'v' && e.ctrlKey) || (e.key === 'v' && e.metaKey)) {
        const pastedText = await navigator.clipboard.readText()
        const characterArray = pastedText.split('')
        let element = e.target
        let count = 0
        while (element) {
            element.value = characterArray[count]
            element = element.nextElementSibling
            count++
        }
    }
    if (e.key === 'ArrowLeft') {
        try {
            if (e.target.selectionStart === 0) {
                e.target.previousElementSibling.focus()
                setTimeout(() => {
                    e.target.previousElementSibling.setSelectionRange(1, 1)
                }, 1)
            }
        } catch (e) {}
    }
    if (e.key === 'ArrowRight') {
        try {
            if (e.target.selectionStart === 1) {
                e.target.nextElementSibling.focus()
            }
        } catch (e) {}
    }
    // Handling Android
    setTimeout(() => {
        if (e.key === 'Unidentified') {
            if (e.target.value.length === 0) {
                setTimeout(() => {
                    try {
                        e.target.previousElementSibling.focus()
                    } catch (e) {}
                }, 10)
            } else if (/[0-9]{1}/.test(e.target.value)) {
                setTimeout(() => {
                    try {
                        e.target.nextElementSibling.focus()
                    } catch (e) {}
                }, 10)
            }
        }
    }, 50)
}
